import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExportFilters, ExportType, useExport } from "@/hooks/useExport";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: ExportType;
  filters: ExportFilters;
  title?: string;
}

export function ExportModal({ isOpen, onClose, type, filters, title }: ExportModalProps) {
  const { t } = useTranslation(["sales", "common"]);
  const {
    status,
    downloadUrl,
    errorMessage,
    startExport,
    cancelExport,
    downloadFile,
    reset,
  } = useExport(type);

  useEffect(() => {
    if (isOpen && status === "idle") {
      startExport(filters);
    }
  }, [isOpen, status, startExport, filters]);

  const handleClose = async () => {
    if (status === "pending") {
      await cancelExport("User closed modal");
    }
    reset();
    onClose();
  };

  const handleDownload = () => {
    downloadFile();
    handleClose();
  };

  const getContent = () => {
    switch (status) {
      case "starting":
      case "pending":
        return (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium">{t("sales:export_modal.processing")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("sales:export_modal.processing_description")}
              </p>
            </div>
          </div>
        );

      case "completed":
        return (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="rounded-full bg-green-500/20 p-3">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <div className="text-center">
              <p className="font-medium">{t("sales:export_modal.completed")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("sales:export_modal.completed_description")}
              </p>
            </div>
          </div>
        );

      case "failed":
        return (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="rounded-full bg-red-500/20 p-3">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <div className="text-center">
              <p className="font-medium">{t("sales:export_modal.failed")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {errorMessage || t("sales:export_modal.failed_description")}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    if (status === "completed" && downloadUrl) {
      return (
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={handleClose}>
            {t("sales:export_modal.close")}
          </Button>
          <Button onClick={handleDownload}>
            <Download className="mr-2 h-4 w-4" />
            {t("sales:export_modal.download_file")}
          </Button>
        </div>
      );
    }

    if (status === "failed") {
      return (
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={handleClose}>
            {t("sales:export_modal.close")}
          </Button>
          <Button onClick={() => startExport(filters)}>
            {t('sales:export_modal.retry')}
          </Button>
        </div>
      );
    }

    if (status === "pending") {
      return (
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={handleClose}>
            {t("sales:export_modal.cancel")}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title || t("sales:export_modal.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {getContent()}
          {getFooterButtons()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
